import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Segmented, Skeleton, message } from 'antd';
import { FullscreenExitOutlined, FullscreenOutlined, LinkOutlined } from '@ant-design/icons';
import { HiClock, HiMap, HiRefresh, HiStatusOffline, HiStatusOnline } from 'react-icons/hi';
import { FaShip } from 'react-icons/fa';
import { formatRefDate } from '../../utils/formatRefDate';
import Maps from './Maps';
import VesselList from './VesselList';
import PositionReports from './PositionReports';
import VesselOperations from './VesselOperations';
import useNearestProvinces from './useNearestProvinces';
import useMonitoringData from './useMonitoringData';
import { filterMonitoringVessels, monitoringSummary, monitoringVessels, positionReports, vesselKey } from './monitoringData';
import './monitoring.css';

const Monitoring = () => {
    const { data: dataJoined, setupLoading: isLoading, currentLoading, refreshLoading, emailLoading, loadError, refresh: fetchAndJoinData } = useMonitoringData();
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [selectedKey, setSelectedKey] = useState('');
    const [vesselCardOpen, setVesselCardOpen] = useState(false);
    const [mapType, setMapType] = useState('map');
    const mapPanel = useRef(null);
    const [fullscreen, setFullscreen] = useState(false);

    useEffect(() => { document.title = 'NextSense Santan - Monitoring'; }, []);
    useEffect(() => {
        const onChange = () => setFullscreen(document.fullscreenElement === mapPanel.current);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const toggleFullscreen = async () => {
        try {
            if (document.fullscreenElement === mapPanel.current) await document.exitFullscreen();
            else if (mapPanel.current?.requestFullscreen) await mapPanel.current.requestFullscreen();
            else message.info('Fullscreen is not supported by this browser.');
        } catch {
            message.info('Fullscreen could not be opened.');
        }
    };

    const reports = useMemo(() => positionReports(dataJoined), [dataJoined]);
    const summary = useMemo(() => monitoringSummary(dataJoined, reports), [dataJoined, reports]);
    const vessels = useMemo(() => monitoringVessels(dataJoined, reports), [dataJoined, reports]);
    const filteredVessels = useMemo(() => filterMonitoringVessels(vessels, search, status), [vessels, search, status]);
    const visibleKeys = useMemo(() => new Set(filteredVessels.map(vessel => vessel.key)), [filteredVessels]);
    const filteredJoined = useMemo(() => dataJoined.filter(vessel => visibleKeys.has(vesselKey(vessel))), [dataJoined, visibleKeys]);
    const provinces = useNearestProvinces(reports);
    const selectedVessel = filteredVessels.find(vessel => vessel.key === selectedKey) || null;
    const selectedProvince = selectedVessel?.report ? provinces.values[JSON.stringify([selectedVessel.report.lat, selectedVessel.report.lng])]?.name : '';
    const busy = refreshLoading;

    useEffect(() => {
        if (selectedKey && !filteredVessels.some(vessel => vessel.key === selectedKey)) {
            setSelectedKey('');
            setVesselCardOpen(false);
        }
    }, [filteredVessels, selectedKey]);

    const selectVessel = useCallback(key => {
        setSelectedKey(key);
        setVesselCardOpen(Boolean(key));
    }, []);

    const metrics = [
        { label: 'Configured Vessels', value: vessels.length, caption: 'Vessels in the configured fleet', icon: FaShip },
        { label: 'Online', value: summary.online, caption: 'Existing API sensor status', icon: HiStatusOnline, tone: 'green', loading: isLoading || currentLoading, share: summary.knownStatuses ? Math.round(summary.online / summary.knownStatuses * 100) : null },
        { label: 'Offline', value: summary.offline, caption: 'Existing API sensor status', icon: HiStatusOffline, tone: 'muted', loading: isLoading || currentLoading, share: summary.knownStatuses ? Math.round(summary.offline / summary.knownStatuses * 100) : null },
        { label: 'Locations Available', value: summary.locations, caption: 'Valid position reports', icon: HiMap, loading: (isLoading || emailLoading) && !reports.length },
        { label: 'Latest Sensor Reading', value: formatRefDate(summary.latest), caption: 'Latest RefDate across sensors', icon: HiClock, date: true, loading: isLoading || currentLoading },
    ];

    return <main className="monitoring-page">
        <section className="monitoring-topbar">
            <header className="monitoring-header">
                <div className="monitoring-heading-copy"><span className="monitoring-header-mark" aria-hidden="true"><FaShip /></span><div>
                    <p className="monitoring-eyebrow">Marine operations control center</p><h1>Vessel Monitoring</h1><span>Fleet positions and sensor status across existing monitoring data</span>
                </div></div>
                <div className="monitoring-header-actions">
                    <div className="monitoring-header-reference"><span>Latest sensor update</span><strong>{formatRefDate(summary.latest)}</strong><small>From loaded sensor data</small></div>
                    <Button className="monitoring-refresh-button" type="primary" icon={<HiRefresh />} loading={busy}
                        aria-label={busy ? 'Refreshing vessel monitoring data' : 'Refresh vessel monitoring data'} aria-live="polite"
                        onClick={fetchAndJoinData}>{busy ? 'Refreshing' : 'Refresh Data'}</Button>
                </div>
            </header>
            <div className="monitoring-summary">
                {metrics.map(({ label, value, caption, icon: Icon, tone = '', date, share, loading }) => <article className={`monitoring-metric ${tone}`} key={label}>
                    <span className="monitoring-icon"><Icon /></span><div className="monitoring-metric__content"><h2>{label}</h2>
                        {loading ? <Skeleton.Input active size="small" /> : <p className={date ? 'monitoring-date-value' : 'monitoring-value'}>{value}</p>}
                        <small>{caption}</small></div>
                    {share !== null && share !== undefined && !loading && <span className="monitoring-metric__share" aria-label={`${share}% of vessels with known status`}>{share}%</span>}
                </article>)}
            </div>
        </section>

        {loadError && <Alert type="error" showIcon message={loadError} action={<Button size="small" onClick={fetchAndJoinData}>Retry</Button>} />}

        <div className={`monitoring-workspace ${vesselCardOpen ? '' : 'is-detail-collapsed'}`}>
            <VesselList vessels={filteredVessels} loading={isLoading} search={search} status={status}
                selectedKey={selectedKey} onSearch={setSearch} onStatus={setStatus} onSelect={selectVessel} />

            <section ref={mapPanel} className="monitoring-map-panel" aria-labelledby="monitoring-map-title">
                <header className="monitoring-panel-heading monitoring-map-heading">
                    <div className="monitoring-panel-title"><span className="monitoring-section-icon" aria-hidden="true"><HiMap /></span><div>
                        <h2 id="monitoring-map-title">Vessel Locations</h2><p>Latest available vessel position reports</p>
                    </div></div>
                    <div className="monitoring-map-actions">
                        {!vesselCardOpen && selectedVessel && <Button icon={<LinkOutlined />} onClick={() => setVesselCardOpen(true)}>Show vessel</Button>}
                        <Segmented className="monitoring-map-type" aria-label="Map presentation" options={[{ label: 'Map', value: 'map' }, { label: 'Satellite', value: 'satellite' }]}
                            value={mapType} onChange={setMapType} />
                        <Button aria-label={fullscreen ? 'Exit map fullscreen' : 'View map in fullscreen'} icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={toggleFullscreen} />
                    </div>
                </header>
                <div className="monitoring-map">
                    <Maps dataSource={filteredJoined} selectedKey={selectedKey} onSelect={selectVessel} mapType={mapType} />
                    {!isLoading && !currentLoading && !filteredVessels.length && <div className="monitoring-map-empty"><strong>No vessels match the current filter</strong><span>Change the search or status filter to restore vessels.</span></div>}
                </div>
                {!reports.length && <p className="monitoring-note">{isLoading || emailLoading ? 'Loading position reports...' : 'No valid vessel locations available.'}</p>}
            </section>

            {vesselCardOpen && <VesselOperations vessel={selectedVessel} province={selectedProvince} provinceLoading={provinces.loading}
                onClose={() => setVesselCardOpen(false)} onUpdated={() => fetchAndJoinData({ queue: true })} />}
        </div>

        <PositionReports reports={reports} loading={(isLoading || emailLoading) && !reports.length} />
    </main>;
};

export default Monitoring;
